import { useState, useMemo } from 'react';
import { AutocompleteInput, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { ShippingStatus } from '../models';
import { Option } from '@/components/inputs/AutocompleteInput';
import { Close as CloseIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface StatusListManagerProps {
  selectedStatusIds: string[];
  availableStatuses: ShippingStatus[];
  onAdd: (statusId: string) => void;
  onRemove: (statusId: string) => void;
  label: string;
  excludeStatusIds?: string[];
  otherListStatusIds?: string[];
  disabled?: boolean;
  requireDestinationStatus?: boolean;
}

const StatusListManager = ({
  selectedStatusIds,
  availableStatuses,
  onAdd,
  onRemove,
  label,
  excludeStatusIds = [],
  otherListStatusIds = [],
  disabled = false,
  requireDestinationStatus = false,
}: StatusListManagerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const availableToAdd = availableStatuses.filter(
    (status) =>
      !selectedStatusIds.includes(status.id) &&
      !excludeStatusIds.includes(status.id) &&
      !otherListStatusIds.includes(status.id)
  );

  const filteredAutocompleteOptions: Option[] = useMemo(() => {
    if (searchQuery.trim().length < 3) {
      return [];
    }
    const cleanSearch = searchQuery.toLowerCase().trim();
    return availableToAdd
      .filter((status) => {
        const name = (status.commercialName || status.name).toLowerCase();
        return name.includes(cleanSearch);
      })
      .map((status) => ({
        label: status.commercialName || status.name,
        data: status.id,
      }));
  }, [availableToAdd, searchQuery]);

  const handleAdd = () => {
    if (selectedOption && !isDisabled) {
      onAdd(selectedOption.data as string);
      setSelectedOption(null);
      setSearchQuery('');
    }
  };

  const getStatusName = (statusId: string): string => {
    const status = availableStatuses.find((s) => s.id === statusId);
    return status?.commercialName || status?.name || statusId;
  };

  const isDisabled =
    disabled || (requireDestinationStatus && excludeStatusIds.length === 0);

  return (
    <div className='flex flex-col gap-4'>
      {requireDestinationStatus && excludeStatusIds.length === 0 && (
        <p className='text-sm text-amber-600 italic'>
          Por favor, seleccione primero el estado destino antes de agregar
          estados.
        </p>
      )}
      <div className='flex gap-2.5'>
        <div className='flex-1'>
          <AutocompleteInput
            name={`${label.toLowerCase().replace(/\s+/g, '-')}-autocomplete`}
            placeholder={label}
            value={selectedOption}
            onChange={(option) => setSelectedOption(option)}
            onInputChange={(val) => setSearchQuery(val)}
            options={filteredAutocompleteOptions}
            disabled={isDisabled || availableToAdd.length === 0}
            showTypingLengthHint={true}
          />
        </div>
        <div className='flex items-end'>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={handleAdd}
            disabled={
              !selectedOption || isDisabled || availableToAdd.length === 0
            }
          >
            Agregar
          </GlobalButton>
        </div>
      </div>

      {selectedStatusIds.length > 0 ? (
        <div className='flex flex-col gap-2'>
          <p className='text-sm font-medium text-gray-700'>
            Estados seleccionados ({selectedStatusIds.length}):
          </p>
          <div className='flex flex-wrap gap-2'>
            {selectedStatusIds.map((statusId) => (
              <span
                key={statusId}
                className='inline-flex items-center gap-2 px-3 py-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 rounded-full text-sm font-medium text-gray-700 transition-all duration-200 shadow-sm'
              >
                <span>{getStatusName(statusId)}</span>
                <IconButton
                  onClick={() => !isDisabled && onRemove(statusId)}
                  size='small'
                  disabled={isDisabled}
                  className='!p-0.5 !min-w-0 !w-5 !h-5 hover:bg-red-50 rounded-full transition-colors'
                >
                  <CloseIcon fontSize='small' className='!text-red-500 hover:!text-red-600' />
                </IconButton>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p className='text-sm text-gray-500 italic'>
          No hay estados seleccionados
        </p>
      )}
    </div>
  );
};

export default StatusListManager;
